<template>
  <div class="question-bank-browser">

    <!-- Controls bar -->
    <div class="controls-bar">
      <LocaleSelector
        v-model="activeLang"
        :locales="SUPPORTED_LOCALES"
        :labels="LOCALE_LABELS"
        aria-label="Display language"
      />

      <div class="controls-right">
        <input
          v-model="searchInput"
          type="search"
          class="search-input"
          placeholder="Search by code or title"
          autocomplete="off"
          spellcheck="false"
        >

        <select v-model="typeFilter" class="select-filter">
          <option value="all">All types</option>
          <option value="numeric_stepper">Numeric stepper</option>
          <option value="single_choice">Single choice</option>
          <option value="matrix">Matrix</option>
          <option value="short_text">Short text</option>
          <option value="number">Number</option>
          <option value="numeric_grid">Numeric grid</option>
        </select>

        <!-- Section filter hidden: sections are now per-questionnaire, so a
             bank-wide section filter (built from each question's vestigial
             home-section, seq_questions.section_id) would list sections from
             every questionnaire — a muddy, broken-looking list. Re-enable if
             the home-section concept is reworked. Default stays "all" so all
             questions show. -->
        <select v-if="false" v-model="sectionFilter" class="select-filter">
          <option value="all">All sections</option>
          <option value="unassigned">Unassigned</option>
          <option
            v-for="opt in sectionFilterOptions"
            :key="opt.key"
            :value="opt.key"
          >{{ opt.label }}</option>
        </select>

        <!-- Picker mode never shows archived rows: archived means retired,
             so a user adding to a new questionnaire shouldn't reach for one. -->
        <label v-if="mode === 'standalone'" class="toggle">
          <input
            v-model="showArchived"
            type="checkbox"
          >
          <span>Show archived</span>
        </label>
      </div>
    </div>

    <!-- Body -->
    <p v-if="loadError" class="error-banner">{{ loadError }}</p>

    <p v-if="loading" class="loading-state">Loading questions…</p>

    <div
      v-else-if="questions.length === 0"
      class="empty-state empty-full"
    >
      <h2 class="empty-title">No questions yet</h2>
      <p v-if="mode === 'standalone'" class="empty-text">
        Create your first question to get started.
      </p>
      <p v-else class="empty-text">
        Create a question from the question bank first.
      </p>
    </div>

    <div
      v-else-if="groupedQuestions.length === 0"
      class="empty-state empty-soft"
    >
      <p>No matches. Adjust filters or search.</p>
      <button
        type="button"
        class="btn-link"
        @click="clearFilters"
      >Clear filters</button>
    </div>

    <div v-else class="section-groups">
      <SectionGroup
        v-for="group in groupedQuestions"
        :key="group.key"
        :group-key="group.key"
        :title="group.label"
        :count="group.count"
        :ephemeral-collapse="mode === 'picker'"
        :initial-collapsed="mode === 'picker'"
      >
        <table class="questions-table">
          <thead>
            <tr>
              <th
                class="th-sortable"
                @click="setSort('code')"
              >
                Code <SortIcon :state="sortState('code')" />
              </th>
              <th
                class="th-sortable"
                @click="setSort('question_type')"
              >
                Type <SortIcon :state="sortState('question_type')" />
              </th>
              <th>Title</th>
              <th v-if="showArchived && group.key !== ARCHIVED_KEY">Status</th>
              <th
                class="th-sortable"
                @click="setSort('updated_at')"
              >
                Updated <SortIcon :state="sortState('updated_at')" />
              </th>
              <th class="th-actions">
                {{ mode === 'picker' ? '' : 'Actions' }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="q in group.rows"
              :key="q.id"
              :class="{ 'row-archived': q.status === 'archived' }"
              @click="onRowClick(q)"
            >
              <td class="td-code mono">{{ q.code }}</td>
              <td>
                <span class="type-chip" :class="`type-${q.question_type}`">
                  {{ typeLabel(q.question_type) }}
                </span>
              </td>
              <td class="td-title">
                <div class="td-title-stack">
                  <span class="title-text">{{ titleDisplay(q) }}</span>
                  <span
                    v-if="excludedQuestionIdSet.has(q.id)"
                    class="excluded-badge"
                    title="This question is already in the section. Adding it again will create a duplicate."
                  >Already in this section</span>
                </div>
              </td>
              <td v-if="showArchived && group.key !== ARCHIVED_KEY">
                <span class="status-pill" :class="`status-${q.status}`">
                  {{ q.status }}
                </span>
              </td>
              <td
                class="td-updated"
                :title="formatAbsolute(q.updated_at)"
              >{{ formatRelative(q.updated_at) }}</td>
              <td class="td-actions" @click.stop>
                <template v-if="mode === 'picker'">
                  <button
                    type="button"
                    class="btn btn-primary btn-sm select-btn"
                    @click="onSelectClick(q)"
                  >Select</button>
                </template>
                <template v-else>
                  <button
                    type="button"
                    class="icon-btn"
                    title="Edit"
                    aria-label="Edit"
                    @click="openEdit(q)"
                  >
                    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
                      <path d="M11.2 2.4l2.4 2.4L5.6 12.8H3.2v-2.4L11.2 2.4z" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>
                    </svg>
                  </button>
                  <button
                    v-if="q.status === 'active'"
                    type="button"
                    class="icon-btn"
                    title="Archive"
                    aria-label="Archive"
                    @click="askArchive(q)"
                  >
                    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
                      <path d="M2 4h12v2H2zM3 6v7h10V6M6 9h4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>
                    </svg>
                  </button>
                  <button
                    v-else
                    type="button"
                    class="icon-btn icon-restore"
                    title="Restore"
                    aria-label="Restore"
                    @click="askRestore(q)"
                  >
                    <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
                      <path d="M3 8a5 5 0 1 0 1.46-3.54M3 3v3h3" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </button>
                </template>
              </td>
            </tr>
          </tbody>
        </table>
      </SectionGroup>
    </div>

    <!-- Archive/restore confirm dialog (standalone mode only; dead code
         in picker mode since the buttons that open it aren't rendered). -->
    <div
      v-if="confirmAction && mode === 'standalone'"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      @click.self="confirmAction = null"
    >
      <div class="modal modal-confirm">
        <h2 id="confirm-title" class="modal-title">
          {{ confirmAction.kind === 'archive' ? 'Archive question?' : 'Restore question?' }}
        </h2>
        <p class="modal-text">
          <template v-if="confirmAction.kind === 'archive'">
            <code class="mono-inline">{{ confirmAction.question.code }}</code>
            will be hidden from the bank. Existing answers and any
            questionnaires that include it are unaffected. You can
            restore it later from "Show archived".
          </template>
          <template v-else>
            <code class="mono-inline">{{ confirmAction.question.code }}</code>
            will become active again and reappear in the question bank.
          </template>
        </p>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="confirmAction.busy"
            @click="confirmAction = null"
          >Cancel</button>
          <button
            type="button"
            :class="confirmAction.kind === 'archive' ? 'btn btn-danger' : 'btn btn-primary'"
            :disabled="confirmAction.busy"
            @click="performConfirm"
          >
            {{ confirmAction.busy
              ? (confirmAction.kind === 'archive' ? 'Archiving…' : 'Restoring…')
              : (confirmAction.kind === 'archive' ? 'Archive' : 'Restore') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Toast for archive/restore feedback. Picker mode never triggers it. -->
    <transition name="toast-fade">
      <div
        v-if="toast"
        class="toast"
        :class="`toast-${toast.kind}`"
        role="status"
        aria-live="polite"
      >{{ toast.text }}</div>
    </transition>
  </div>
</template>

<script>
import apiService from '@/services/apiService';
import LocaleSelector from '@/components/admin/LocaleSelector.vue';
import SectionGroup from '@/components/admin/SectionGroup.vue';
import {
  questionsState,
  localizeI18n,
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  LOCALE_LABELS,
} from '@/components/admin/_data/questions.js';
import {
  UNASSIGNED_KEY,
  ARCHIVED_KEY,
} from '@/components/admin/_data/questionBankSections.js';

// Inline sort indicator (asc/desc/none).
const SortIcon = {
  name: 'SortIcon',
  props: { state: { type: String, default: 'none' } },
  template: `
    <span class="sort-icon" :class="'sort-' + state" aria-hidden="true">
      <template v-if="state === 'asc'">▲</template>
      <template v-else-if="state === 'desc'">▼</template>
      <template v-else>↕</template>
    </span>
  `,
};

const TYPE_LABELS = {
  numeric_stepper: 'Stepper',
  single_choice: 'Single choice',
  matrix: 'Matrix',
  short_text: 'Short text',
  number: 'Number',
  numeric_grid: 'Numeric grid',
};

const dateFmt = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export default {
  name: 'QuestionBankBrowser',
  components: { LocaleSelector, SectionGroup, SortIcon },
  props: {
    // 'standalone' = bank page experience (edit + archive row actions,
    //                row click navigates to edit, archive toggle visible).
    // 'picker' = modal experience (Select button + whole-row click emits
    //            select-question, no archive toggle, no row actions for
    //            edit/archive — clean choosing flow).
    mode: {
      type: String,
      default: 'standalone',
      validator: (v) => v === 'standalone' || v === 'picker',
    },
    // Picker-only: question ids to exclude from display. For Step 1
    // unused; Step 2 will pass the ids already attached to the section
    // so they don't appear (or appear with an "already in this section"
    // affordance — TBD in Step 2).
    excludeQuestionIds: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['select-question'],
  data() {
    return {
      SUPPORTED_LOCALES,
      LOCALE_LABELS,
      UNASSIGNED_KEY,
      ARCHIVED_KEY,
      activeLang: DEFAULT_LOCALE,
      searchInput: '',
      searchQuery: '',
      searchDebounceTimer: null,
      typeFilter: 'all',
      sectionFilter: 'all',
      // Forced false in picker mode (the toggle is hidden anyway, but
      // the rest of the filtering pipeline reads this flag).
      showArchived: false,
      // Single global sort applied uniformly to every section group.
      sortKey: 'updated_at',
      sortDir: 'desc',
      loading: false,
      loadError: null,
      confirmAction: null,
      toast: null,
      toastTimer: null,
    };
  },
  computed: {
    questions() {
      // Junction Step 3: excluded ids are an *informational* signal —
      // the row stays visible and clickable, with an "Already in this
      // section" badge. Duplicates are allowed. The previous filter-out
      // behaviour from Step 1 has been replaced; the prop is still
      // honoured, just less aggressively.
      const items = questionsState.items;
      const rows = this.showArchived ? items : items.filter(q => q.status === 'active');
      return rows;
    },
    excludedQuestionIdSet() {
      return new Set(this.excludeQuestionIds || []);
    },
    sectionFilterOptions() {
      const seen = new Map();
      for (const q of questionsState.items) {
        if (!q.section_id) continue;
        const label = localizeI18n(q.section_name, this.activeLang)
          || `Section ${q.section_id}`;
        const key = `id:${q.section_id}`;
        if (!seen.has(key)) seen.set(key, { key, label, id: q.section_id });
      }
      return Array.from(seen.values()).sort((a, b) =>
        a.label.localeCompare(b.label),
      );
    },
    filteredQuestions() {
      let rows = this.questions;

      if (this.typeFilter !== 'all') {
        rows = rows.filter(q => q.question_type === this.typeFilter);
      }

      // sectionFilter stays 'all' in practice: the <select> that sets it is
      // disabled and picker mode no longer pre-applies it. Kept because the
      // state and the select are still here for whenever the home-section
      // concept is reworked.
      if (this.sectionFilter !== 'all') {
        if (this.sectionFilter === 'unassigned') {
          rows = rows.filter(q => !q.section_id);
        } else if (this.sectionFilter.startsWith('id:')) {
          const wantedId = Number(this.sectionFilter.slice(3));
          rows = rows.filter(q => q.section_id === wantedId);
        }
      }

      const q = this.searchQuery.trim().toLowerCase();
      if (q) {
        rows = rows.filter(row => {
          if (row.code && row.code.toLowerCase().includes(q)) return true;
          const title = row.content?.title || {};
          for (const loc of SUPPORTED_LOCALES) {
            const v = title[loc];
            if (typeof v === 'string' && v.toLowerCase().includes(q)) {
              return true;
            }
          }
          return false;
        });
      }

      const key = this.sortKey;
      const dir = this.sortDir === 'asc' ? 1 : -1;
      const getKey = (r) => this._sortValue(r, key);
      return [...rows].sort((a, b) => {
        const av = getKey(a);
        const bv = getKey(b);
        if (av === bv) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return av < bv ? -dir : dir;
      });
    },
    groupedQuestions() {
      const active = [];
      const archived = [];
      for (const q of this.filteredQuestions) {
        if (q.status === 'archived') archived.push(q);
        else active.push(q);
      }

      const bySection = new Map();
      for (const q of active) {
        const k = q.section_id != null ? q.section_id : null;
        if (!bySection.has(k)) bySection.set(k, []);
        bySection.get(k).push(q);
      }

      const realKeys = Array.from(bySection.keys()).filter(k => k != null);
      realKeys.sort((a, b) => {
        const an = this._englishSectionName(a) || `~${a}`;
        const bn = this._englishSectionName(b) || `~${b}`;
        return an.localeCompare(bn);
      });

      const groups = [];
      for (const sid of realKeys) {
        const rows = bySection.get(sid);
        if (!rows || !rows.length) continue;
        groups.push({
          key: String(sid),
          label: this.sectionDisplayById(sid),
          count: rows.length,
          rows,
        });
      }

      const unassignedRows = bySection.get(null) || [];
      if (unassignedRows.length) {
        groups.push({
          key: UNASSIGNED_KEY,
          label: 'Unassigned',
          count: unassignedRows.length,
          rows: unassignedRows,
        });
      }

      if (this.showArchived && archived.length) {
        groups.push({
          key: ARCHIVED_KEY,
          label: 'Archived',
          count: archived.length,
          rows: archived,
        });
      }

      return groups;
    },
  },
  watch: {
    searchInput(newValue) {
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.searchQuery = newValue;
      }, 250);
    },
  },
  created() {
    this.fetchAll();
  },
  beforeUnmount() {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
  methods: {
    async fetchAll() {
      this.loading = true;
      this.loadError = null;
      try {
        const rows = await apiService.fetchQuestions({ status: 'all' });
        questionsState.items.splice(0, questionsState.items.length, ...rows);
        if (!questionsState.sections.length) {
          apiService.fetchSections().then(sections => {
            questionsState.sections.splice(
              0,
              questionsState.sections.length,
              ...sections,
            );
          }).catch(e => {
            console.warn('Could not load sections:', e);
          });
        }
        // Picker mode deliberately does NOT pre-filter by section. It used
        // to pin sectionFilter to `defaultSectionId`, which hid 13 to 16 of
        // 17 bank questions on every section and had no way out, because the
        // section <select> above is disabled. Two reasons not to bring it
        // back: the focus it was reaching for is already provided by the
        // collapsed section groups (see :initial-collapsed below), and
        // seq_questions.section_id is a vestigial home-section that says
        // nothing about which questionnaire section a question may join —
        // questions are reusable library objects, so matching on it fights
        // the library. Search and the type filter are the visible, clearable
        // ways to narrow the list.
      } catch (e) {
        const detail = e?.response?.data?.detail;
        this.loadError = typeof detail === 'string'
          ? detail
          : 'Failed to load questions.';
      } finally {
        this.loading = false;
      }
    },

    _sortValue(row, key) {
      switch (key) {
        case 'code':
          return (row.code || '').toLowerCase();
        case 'question_type':
          return TYPE_LABELS[row.question_type] || row.question_type || '';
        case 'updated_at':
          return row.updated_at || '';
        default:
          return row[key] || '';
      }
    },

    _englishSectionName(sectionId) {
      if (sectionId == null) return null;
      const fromSections = questionsState.sections.find(s => s.id === sectionId);
      if (fromSections && fromSections.name) {
        const en = (fromSections.name.en || '').trim();
        if (en) return en;
        for (const loc of ['de', 'fr', 'it']) {
          const v = (fromSections.name[loc] || '').trim();
          if (v) return v;
        }
      }
      for (const q of questionsState.items) {
        if (q.section_id === sectionId && q.section_name) {
          const en = (q.section_name.en || '').trim();
          if (en) return en;
          for (const loc of ['de', 'fr', 'it']) {
            const v = (q.section_name[loc] || '').trim();
            if (v) return v;
          }
        }
      }
      return null;
    },

    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = key === 'updated_at' ? 'desc' : 'asc';
      }
    },

    sortState(key) {
      if (this.sortKey !== key) return 'none';
      return this.sortDir;
    },

    clearFilters() {
      this.searchInput = '';
      this.searchQuery = '';
      this.typeFilter = 'all';
      this.sectionFilter = 'all';
      // showArchived is forced false in picker mode anyway. In standalone
      // we also reset it on "Clear filters" — same as before the refactor.
      this.showArchived = false;
    },

    typeLabel(t) {
      return TYPE_LABELS[t] || t;
    },

    sectionDisplayById(sectionId) {
      const fromSections = questionsState.sections.find(s => s.id === sectionId);
      if (fromSections) {
        return localizeI18n(fromSections.name, this.activeLang)
          || `Section ${sectionId}`;
      }
      for (const q of questionsState.items) {
        if (q.section_id === sectionId && q.section_name) {
          return localizeI18n(q.section_name, this.activeLang)
            || `Section ${sectionId}`;
        }
      }
      return `Section ${sectionId}`;
    },

    titleDisplay(q) {
      const title = q.content?.title || {};
      return title[this.activeLang] || '';
    },

    formatRelative(iso) {
      if (!iso) return '';
      const then = new Date(iso).getTime();
      const now = Date.now();
      const diff = Math.max(0, now - then) / 1000;
      if (diff < 60) return 'just now';
      if (diff < 3600) {
        const m = Math.round(diff / 60);
        return `${m} minute${m === 1 ? '' : 's'} ago`;
      }
      if (diff < 86400) {
        const h = Math.round(diff / 3600);
        return `${h} hour${h === 1 ? '' : 's'} ago`;
      }
      if (diff < 86400 * 30) {
        const d = Math.round(diff / 86400);
        return `${d} day${d === 1 ? '' : 's'} ago`;
      }
      if (diff < 86400 * 365) {
        const m = Math.round(diff / 86400 / 30);
        return `${m} month${m === 1 ? '' : 's'} ago`;
      }
      const y = Math.round(diff / 86400 / 365);
      return `${y} year${y === 1 ? '' : 's'} ago`;
    },

    formatAbsolute(iso) {
      if (!iso) return '';
      return dateFmt.format(new Date(iso));
    },

    /* Row click — branches on mode. Same dual pattern as standalone:
       clicking the row triggers the primary action for that mode. */
    onRowClick(q) {
      if (this.mode === 'picker') {
        this.$emit('select-question', q);
      } else {
        this.openEdit(q);
      }
    },

    onSelectClick(q) {
      // Explicit picker affordance. Same effect as clicking the row;
      // duplicated so screen readers / keyboard users have a focusable
      // primary action per row.
      this.$emit('select-question', q);
    },

    openEdit(q) {
      this.$router.push(`/admin/questions/${q.id}/edit`);
    },

    askArchive(q) {
      this.confirmAction = { kind: 'archive', question: q, busy: false };
    },
    askRestore(q) {
      this.confirmAction = { kind: 'restore', question: q, busy: false };
    },

    async performConfirm() {
      if (!this.confirmAction) return;
      const { kind, question } = this.confirmAction;
      this.confirmAction.busy = true;
      try {
        const updated = kind === 'archive'
          ? await apiService.archiveQuestion(question.id)
          : await apiService.restoreQuestion(question.id);
        const idx = questionsState.items.findIndex(q => q.id === question.id);
        if (idx !== -1) {
          questionsState.items.splice(idx, 1, updated);
        }
        this.confirmAction = null;
        this.showToast('success',
          kind === 'archive' ? 'Question archived.' : 'Question restored.');
      } catch (e) {
        const detail = e?.response?.data?.detail;
        this.confirmAction.busy = false;
        this.showToast('error', typeof detail === 'string'
          ? detail
          : `Failed to ${kind} question.`);
      }
    },

    showToast(kind, text) {
      if (this.toastTimer) clearTimeout(this.toastTimer);
      this.toast = { kind, text };
      const ms = kind === 'error' ? 6000 : 3500;
      this.toastTimer = setTimeout(() => {
        this.toast = null;
        this.toastTimer = null;
      }, ms);
    },
  },
};
</script>

<style scoped>
.question-bank-browser {
  --color-bg-card: #ffffff;
  --color-bg-muted: #f5f4ef;
  --color-bg-soft: #fafaf7;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-border-strong: rgba(0, 0, 0, 0.2);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --color-accent-dark: #3F368F;
  --color-bad: #C8392E;
  --color-bad-dark: #A92F26;
  --color-bad-soft: rgba(200, 57, 46, 0.10);
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --font-mono: "SF Mono", Menlo, Consolas, monospace;

  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.6;
  box-sizing: border-box;
  /* Flex column so the section-groups area can fill available vertical
     space inside a constrained parent (the modal) and scroll internally.
     In the standalone page the parent has no height constraint, so
     children just stack to their natural height — no visible difference. */
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.question-bank-browser *,
.question-bank-browser *::before,
.question-bank-browser *::after {
  box-sizing: border-box;
}

.btn {
  padding: 9px 16px;
  border-radius: var(--radius-md);
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  border: 0.5px solid var(--color-border);
  transition: background 0.15s, border-color 0.15s, color 0.15s, opacity 0.15s;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.btn-sm {
  padding: 5px 12px;
  font-size: 12px;
}

.btn-primary {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-accent-dark);
  border-color: var(--color-accent-dark);
}

.btn-secondary {
  background: var(--color-bg-card);
  color: var(--color-text-primary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--color-bg-soft);
  border-color: var(--color-border-strong);
}

.btn-danger {
  background: var(--color-bad);
  color: white;
  border-color: var(--color-bad);
}

.btn-danger:hover:not(:disabled) {
  background: var(--color-bad-dark);
  border-color: var(--color-bad-dark);
}

.btn-link {
  background: none;
  border: none;
  color: var(--color-accent);
  cursor: pointer;
  font-size: 13px;
  padding: 0;
  font-family: inherit;
  text-decoration: underline;
}

.select-btn {
  /* Stop the inherited block-grow + match the icon-btn vertical rhythm. */
  white-space: nowrap;
}

/* ───────── Controls bar ───────── */
.controls-bar {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
  /* Keep its natural height inside the flex column. The section-groups
     area below it owns the leftover space. */
  flex-shrink: 0;
}

.controls-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  flex-wrap: wrap;
}

.search-input {
  flex: 1 1 200px;
  padding: 8px 12px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  font-family: inherit;
  font-size: 13px;
  color: var(--color-text-primary);
  min-width: 0;
}

.search-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 2px var(--color-accent-soft);
}

.select-filter {
  padding: 8px 10px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  font-family: inherit;
  font-size: 13px;
  color: var(--color-text-primary);
  cursor: pointer;
}

.toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--color-text-secondary);
  cursor: pointer;
  user-select: none;
}

.toggle input {
  accent-color: var(--color-accent);
  cursor: pointer;
}

/* ───────── Banners and states ───────── */
.error-banner {
  font-size: 13px;
  color: #6B1F18;
  background: var(--color-bad-soft);
  border: 0.5px solid rgba(200, 57, 46, 0.45);
  border-radius: var(--radius-md);
  padding: 10px 14px;
  margin: 0 0 1.25rem;
}

.loading-state {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--color-text-tertiary);
  font-size: 14px;
}

.empty-state {
  text-align: center;
  color: var(--color-text-secondary);
}

.empty-full {
  padding: 4rem 1rem;
  background: var(--color-bg-soft);
  border: 0.5px dashed var(--color-border);
  border-radius: var(--radius-lg);
}

.empty-soft {
  padding: 2.5rem 1rem;
  font-size: 14px;
}

.empty-soft p {
  margin: 0 0 8px;
}

.empty-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 6px;
  color: var(--color-text-primary);
}

.empty-text {
  font-size: 14px;
  margin: 0 0 1.25rem;
}

/* ───────── Section groups ───────── */
.section-groups {
  /* Block flow (not flex) so the section-group children stack at their
     natural heights instead of shrinking to fit a constrained parent.
     Inside a height-constrained ancestor (the modal), without this the
     flex-shrink default would compress every section group to fit and
     never trigger overflow — exactly the "no scrollbar" failure we
     hit on first try. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

.questions-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.questions-table th,
.questions-table td {
  padding: 10px 12px;
  text-align: left;
  border-bottom: 0.5px solid var(--color-border);
}

.questions-table th {
  background: var(--color-bg-muted);
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--color-text-tertiary);
  position: sticky;
  top: 0;
  z-index: 1;
}

.th-sortable {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.th-sortable:hover {
  color: var(--color-text-secondary);
}

.sort-icon {
  display: inline-block;
  margin-left: 4px;
  font-size: 10px;
  opacity: 0.4;
}

.sort-icon.sort-asc,
.sort-icon.sort-desc {
  opacity: 1;
  color: var(--color-accent);
}

.th-actions {
  text-align: right;
  white-space: nowrap;
}

.questions-table tbody tr {
  cursor: pointer;
  transition: background 0.1s;
}

.questions-table tbody tr:hover {
  background: var(--color-bg-soft);
}

.row-archived {
  opacity: 0.65;
}

.td-code {
  white-space: nowrap;
}

.mono,
.mono-inline {
  font-family: var(--font-mono);
  font-size: 12px;
  letter-spacing: 0.02em;
}

.mono-inline {
  background: var(--color-bg-muted);
  padding: 2px 6px;
  border-radius: 4px;
}

.td-title {
  max-width: 380px;
  overflow: hidden;
}

/* Stack the title and the optional "Already in this section" badge in
   a flex column. Put flex on this inner wrapper, NOT on the <td> —
   otherwise the cell falls out of `display: table-cell` and the row's
   border-bottom on this column shifts relative to its neighbours. */
.td-title-stack {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.excluded-badge {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-tertiary);
  background: var(--color-bg-muted);
  padding: 1px 6px;
  border-radius: 4px;
  align-self: flex-start;
}

.title-text {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.td-updated {
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.td-actions {
  text-align: right;
  white-space: nowrap;
}

.icon-btn {
  background: transparent;
  border: 0.5px solid transparent;
  border-radius: 4px;
  padding: 5px 7px;
  cursor: pointer;
  color: var(--color-text-secondary);
  margin-left: 2px;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.icon-btn:hover {
  background: var(--color-bg-muted);
  color: var(--color-accent);
  border-color: var(--color-border);
}

.icon-restore:hover {
  color: var(--color-accent);
}

.type-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
}

.type-chip.type-numeric_stepper {
  background: var(--color-accent-soft);
  color: var(--color-accent-dark);
}

.type-chip.type-short_text {
  background: #E4F0E9;
  color: #2E7D5B;
}

.type-chip.type-numeric_grid {
  background: #FDF0E3;
  color: #8A5A12;
}

.type-chip.type-number {
  background: #FBEEDD;
  color: #8A5A12;
}

.status-pill {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 2px 7px;
  border-radius: 4px;
}

.status-pill.status-active {
  background: var(--color-accent-soft);
  color: var(--color-accent-dark);
}

.status-pill.status-archived {
  background: var(--color-bg-soft);
  color: var(--color-text-tertiary);
}

/* ───────── Modal + toast (archive/restore in standalone mode) ───────── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  z-index: 2000;
}

.modal {
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1.5rem;
  max-width: 460px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px;
}

.modal-text {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0 0 1.25rem;
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.toast {
  position: fixed;
  left: 50%;
  bottom: 28px;
  transform: translateX(-50%);
  background: var(--color-text-primary);
  color: white;
  padding: 11px 18px;
  border-radius: var(--radius-md);
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  max-width: min(90vw, 520px);
  z-index: 3000;
}

.toast-success { background: #2E7D32; }
.toast-error { background: var(--color-bad-dark); }

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}
</style>
